import { useState, useEffect } from 'react';
import Modal from '../shared/Modal';
import { sendTeamEmail } from '../../utils/api';

export default function SendTeamEmailModal({ open, onClose, teamId, members, senderEmail, showToast }) {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [sending, setSending] = useState(false);
  const [progress, setProgress] = useState('');

  useEffect(() => {
    if (!open) return;
    // Default: select all members with an email
    setSelected(new Set(
      (members || []).filter(m => m.email).map(m => m.id)
    ));
    setSubject('');
    setBody('');
  }, [open, members]);

  function toggleAll() {
    const emailable = (members || []).filter(m => m.email).map(m => m.id);
    if (selected.size === emailable.length) setSelected(new Set());
    else setSelected(new Set(emailable));
  }

  function toggleOne(id) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function handleSend() {
    if (!subject.trim()) { showToast('Subject is required.'); return; }
    if (!body.trim()) { showToast('Message body is required.'); return; }
    const to = (members || []).filter(m => m.email && selected.has(m.id)).map(m => ({ id: m.id, email: m.email, name: m.name }));
    if (to.length === 0) { showToast('No recipients selected.'); return; }
    setSending(true);
    // Send in chunks: one request for a full roster outlives the proxy's
    // request timeout (each email is sent individually server-side), so a big
    // send has to be several small ones.
    const CHUNK = 10;
    let sent = 0;
    const errors = [];
    try {
      for (let i = 0; i < to.length; i += CHUNK) {
        const slice = to.slice(i, i + CHUNK);
        setProgress(to.length > CHUNK ? `Sending ${Math.min(i + CHUNK, to.length)} of ${to.length}…` : '');
        try {
          const result = await sendTeamEmail(teamId, slice, subject.trim(), body.trim(), senderEmail);
          sent += result?.sent || 0;
          (result?.errors || []).forEach(e => errors.push(e));
        } catch (err) {
          slice.forEach(r => errors.push(`${r.email}: ${err.message || 'request failed'}`));
        }
      }
      if (sent === 0) {
        showToast('Failed to send — ' + (errors[0] || 'unknown error'));
      } else if (errors.length > 0) {
        showToast(`Sent to ${sent} · ${errors.length} failed.`);
        onClose();
      } else {
        showToast(`Email sent to ${sent} ${sent === 1 ? 'person' : 'people'}.`);
        onClose();
      }
    } finally {
      setSending(false);
      setProgress('');
    }
  }

  const emailableMembers = (members || []).filter(m => m.email);
  const noEmailMembers = (members || []).filter(m => !m.email);
  const selectedCount = emailableMembers.filter(m => selected.has(m.id)).length;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Send Email to Team"
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={sending}>Cancel</button>
          <button className="btn btn-dark" onClick={handleSend} disabled={sending || selectedCount === 0}>
            {sending ? (progress || 'Sending…') : `Send to ${selectedCount}`}
          </button>
        </>
      }
    >
      {/* Recipients */}
      <div className="form-group">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <label className="form-label" style={{ marginBottom: 0 }}>Recipients</label>
          <button
            type="button"
            onClick={toggleAll}
            style={{ fontFamily: 'var(--font-h)', fontSize: 11, fontWeight: 700, color: 'var(--muted)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
          >
            {selectedCount === emailableMembers.length ? 'Deselect all' : 'Select all'}
          </button>
        </div>
        <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden', maxHeight: 200, overflowY: 'auto' }}>
          {emailableMembers.map((m, i) => (
            <label
              key={m.id}
              style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', cursor: 'pointer',
                borderBottom: i < emailableMembers.length - 1 ? '1px solid var(--border)' : 'none',
                background: selected.has(m.id) ? 'var(--off)' : 'var(--white)',
              }}
            >
              <input
                type="checkbox"
                checked={selected.has(m.id)}
                onChange={() => toggleOne(m.id)}
                style={{ flexShrink: 0 }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontFamily: 'var(--font-h)', fontSize: 13, fontWeight: 700, color: 'var(--black)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {m.name}
                </div>
                <div style={{ fontFamily: 'var(--font-b)', fontSize: 12, color: 'var(--muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {m.email}
                </div>
              </div>
            </label>
          ))}
          {emailableMembers.length === 0 && (
            <div style={{ padding: '12px 16px', fontFamily: 'var(--font-b)', fontSize: 13, color: 'var(--muted)' }}>
              No team members with email addresses.
            </div>
          )}
        </div>
        {noEmailMembers.length > 0 && (
          <div style={{ fontFamily: 'var(--font-b)', fontSize: 12, fontWeight: 300, color: 'var(--muted)', marginTop: 6 }}>
            {noEmailMembers.length} member{noEmailMembers.length !== 1 ? 's' : ''} without an email address will not receive this.
          </div>
        )}
      </div>

      {/* Subject */}
      <div className="form-group">
        <label className="form-label">Subject</label>
        <input
          className="form-input"
          value={subject}
          onChange={e => setSubject(e.target.value)}
          placeholder="e.g. Important update for this weekend…"
        />
      </div>

      {/* Body */}
      <div className="form-group">
        <label className="form-label">Message</label>
        <textarea
          className="form-input"
          rows={6}
          value={body}
          onChange={e => setBody(e.target.value)}
          placeholder="Write your message here…"
          style={{ resize: 'vertical', fontFamily: 'var(--font-b)', fontWeight: 300, lineHeight: 1.6 }}
        />
      </div>
    </Modal>
  );
}
